/**
 * Auth Context
 * Provides authentication state to entire application
 * Uses React Context API for state management
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authService, UserInfo } from '../services/authService';

export interface AuthContextType {
  isAuthenticated: boolean;
  user: UserInfo | null;
  loading: boolean;
  error: string | null;
  login: (returnUrl?: string) => void;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider Component
 * Wraps application and provides auth state to all children
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check authentication status on mount - Pure client-side
  useEffect(() => {
    let isMounted = true;
    
    // Failsafe: Force loading to false after 10 seconds
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        console.warn('[AuthContext] Timeout reached after 10s, forcing loading=false');
        setLoading(false);
        setError('Authentication timeout - please refresh the page');
      }
    }, 10000);
    
    const checkAuth = async () => {
      if (!isMounted) return;
      
      try {
        setLoading(true);
        setError(null);

        console.log('[AuthContext] Checking client-side authentication status...');
        const authStatus = await authService.getAuthStatus();
        
        if (!isMounted) return;
        
        console.log('[AuthContext] Auth status received:', {
          isAuthenticated: authStatus.isAuthenticated,
          hasUser: !!authStatus.user,
          username: authStatus.user?.username
        });
        
        if (authStatus.isAuthenticated && authStatus.user) {
          // User is authenticated via SSO
          setIsAuthenticated(true);
          setUser(authStatus.user);
          console.log('[AuthContext] User authenticated:', authStatus.user.username);
        } else {
          // User is not authenticated
          setIsAuthenticated(false);
          setUser(null);
          console.log('[AuthContext] User not authenticated');
          
          if (authStatus.error) setError(authStatus.error);
        }
      } catch (err) {
        if (!isMounted) return;
        
        console.error('[AuthContext] Error checking authentication:', err);
        setIsAuthenticated(false);
        setUser(null);
        
        const errorMessage = err instanceof Error ? err.message : 'Failed to check authentication status';
        setError(errorMessage);
      } finally {
        if (isMounted) {
          clearTimeout(timeoutId);
          setLoading(false);
        }
      }
    };

    // Check authentication immediately
    checkAuth();
    
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, []);

  const login = useCallback(async (returnUrl?: string) => {
    try {
      setError(null);
      await authService.initiateSSOLogin(returnUrl);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login initiation failed';
      setError(errorMessage);
      console.error('Login error:', errorMessage);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setError(null);
      setIsAuthenticated(false);
      setUser(null);
      authService.clearCache();
      authService.logout();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Logout failed';
      setError(errorMessage);
      console.error('Logout error:', errorMessage);
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      setError(null);
      console.log('[AuthContext] Refreshing authentication status...');
      const authStatus = await authService.getAuthStatus();
      console.log('[AuthContext] Refresh result:', {
        isAuthenticated: authStatus.isAuthenticated,
        hasUser: !!authStatus.user,
        username: authStatus.user?.username
      });
      setIsAuthenticated(authStatus.isAuthenticated);
      setUser(authStatus.user || null);
      
      if (authStatus.isAuthenticated) {
        console.log('[AuthContext] Authentication refresh successful');
      } else {
        console.warn('[AuthContext] Authentication refresh returned not authenticated');
        if (authStatus.error) {
          setError(authStatus.error);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Auth refresh failed';
      setError(errorMessage);
      console.error('[AuthContext] Refresh error:', errorMessage);
      setIsAuthenticated(false);
      setUser(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    error,
    login,
    logout,
    refreshAuth,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use Auth Context
 * Must be used within AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
